import { Component, Vue, Prop, Watch } from 'vue-property-decorator'

export type ValidateFn = (value: any) => any
export type ValidateAsyncFn = (value: any) => Promise<any>

@Component
export default class Validatable extends Vue {
	@Prop() readonly value!: any
	@Prop({ default: Array }) readonly rules!: ValidateFn[]
	@Prop({ default: Array }) readonly asyncRules!: ValidateAsyncFn[]
	@Prop({ default: true }) readonly autoValidate!: boolean
	protected isAsyncValidation: boolean = false
	protected catchError: boolean = false
	protected errorMsg?: string | null = null
	protected defaultRules: ValidateFn[] = []
	protected promise!: Promise<boolean> | null

	@Watch('value')
	private runAutoValidate() {
		this.errorMsg = null
		if (!this.autoValidate) return
		this.validateSync()
	}

	hasAsyncRules() {
		return Boolean(this.asyncRules && this.asyncRules.length)
	}

	async validate() {
		const result = this.validateSync() && await this.validateAsync()
		return result
	}

	validateSync() {
		const rules = [
			...this.defaultRules,
			...this.rules,
		]

		return rules.every(rule => {
			const result = rule(this.value)
			if (result && typeof result === 'string') {
				this.errorMsg = result
				return false
			}
			return result
		})
	}

	async validateAsync() {
		if (!this.hasAsyncRules) return true

		if (this.promise) return this.promise
		if (this.isAsyncValidation || typeof this.errorMsg === 'string') return false

		this.errorMsg = null
		this.isAsyncValidation = true

		const promises: Array<Promise<boolean>> = []

		this.asyncRules.every(async rule => {
			const promise: Promise<boolean> = new Promise(async (resolve) => {
				let result
				try {
					result = await rule(this.value)
				} catch (e) {
					this.catchError = true
					resolve(false)
				}
				if (typeof result === 'string') {
					this.errorMsg = result
					resolve(false)
				}
				resolve(result)
			})
			promises.push(promise)
		})

		this.promise = Promise.all(promises)
			.then(values => {
				this.isAsyncValidation = false
				this.promise = null
				return values.indexOf(false) < 0
			})

		return this.promise
	}

	async resetValidation() {
		await this.$nextTick()
		this.errorMsg = null
		this.catchError = false
	}

	async reset() {
		this.$emit('input', void 0)
		this.$emit('change', void 0)
		await this.resetValidation()
	}

	created() {
		this.$on('change', this.validateAsync)
	}

	mounted() {
		if (this.value) this.validate()
	}

	beforeDestroy() {
		this.$off('change', this.validateAsync)
	}
}
